import { useParams } from '@tanstack/react-router'
import type { NodeProps } from '@xyflow/react'
import { CantileverButton } from 'components/CantileverButton'
import {
  CustomSymbolNode,
  type CustomSymbolTemplate,
} from 'features/processFlowDiagrams/components/nodes/CustomSymbolNode'
import {
  registerCustomSymbol,
  SYMBOL_CATEGORIES,
  SymbolDefinition,
} from 'features/processFlowDiagrams/data/MinimalSymbolLibrary'
import { DrawerState, useRightDrawer } from 'features/rightDrawer'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import { ChevronDown, ChevronRight, Plus, Search } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { type PfdNode, RolodexEntryType } from 'types'

const LibraryContainer = styled.div`
  width: 16rem;
  height: 100%;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-right: 1px solid ${({ theme }) => theme.forecast.background1};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const LibraryHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border2};
  background-color: ${({ theme }) => theme.forecast.background1};
`

const LibraryTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.text1};
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: ${({ theme }) =>
    theme.forecast.cardBackground || theme.forecast.background2};
  color: ${({ theme }) => theme.forecast.text1};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.text1};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.forecast.text1 + '1A'};
  }

  &::placeholder {
    color: ${({ theme }) => theme.forecast.text3};
  }
`

const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.5rem;
  width: 1rem;
  height: 1rem;
  color: ${({ theme }) => theme.forecast.text3};
  pointer-events: none;
`

const CategoriesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.5rem;
  max-height: calc(100vh - 200px);
`

const CategoryHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.text1};
  background-color: ${(props) =>
    props.$isExpanded ? props.theme.forecast.background3 : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
  }
`

const CategoryContent = styled.div<{ $isExpanded: boolean }>`
  display: ${(props) => (props.$isExpanded ? 'block' : 'none')};
  padding-left: 0.5rem;
  margin-bottom: 0.5rem;
`

const SymbolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(3.5rem, 1fr));
  gap: 0.5rem;
  padding: 0.25rem;
`

const SymbolItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem;
  border-radius: 0.375rem;
  cursor: grab;
  background-color: ${({ theme }) =>
    theme.forecast.cardBackground || theme.forecast.background1};
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.text1};
    box-shadow: 0 2px 4px
      ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.1)'};
    transform: translateY(-1px);
  }

  &:active {
    cursor: grabbing;
    transform: translateY(0);
  }
`

const SymbolPreview = styled.div`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.25rem;
  color: ${({ theme }) => theme.forecast.text1};

  svg {
    path,
    circle,
    ellipse,
    line,
    rect {
      stroke-width: 6 !important;
    }
  }
`

const SymbolLabel = styled.div`
  font-size: 0.625rem;
  text-align: center;
  color: ${({ theme }) => theme.forecast.text3};
  line-height: 1.2;
  max-width: 100%;
  word-wrap: break-word;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: ${({ theme }) => theme.forecast.text3};
  font-size: 0.875rem;
  text-align: center;
`

const EmptyStateIcon = styled(Search)`
  margin-bottom: 0.5rem;
  opacity: 0.5;
`

const EmptyStateSubtext = styled.div`
  font-size: 0.75rem;
  margin-top: 0.25rem;
  opacity: 0.7;
`

const FooterContainer = styled.div`
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.text3};
  border-top: 1px solid ${({ theme }) => theme.forecast.border2};
  background-color: ${({ theme }) => theme.forecast.background3};
`

interface CategorizedNodeLibraryProps {
  onDragStart: (event: React.DragEvent, symbolId: string) => void
  onImportNode?: () => void
}

export const CategorizedNodeLibrary: React.FC<CategorizedNodeLibraryProps> = ({
  onDragStart,
  onImportNode,
}) => {
  const { setDrawerState } = useRightDrawer()
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['instruments', 'valves']) // Default expanded categories
  )

  // Scope custom nodes to the current site (if available)
  const { siteID } = useParams({ strict: false })
  const customQuery = useMemo(
    () =>
      siteID
        ? {
            type: RolodexEntryType.CUSTOM_PFD_NODE,
            tags: { site: siteID },
            limit: 1000,
          }
        : { type: RolodexEntryType.CUSTOM_PFD_NODE, limit: 1000 },
    [siteID]
  )
  const { entries: customEntries } = useRolodexEntries(customQuery)

  // Prepare signed URLs for any image-backed templates
  const imageFilePaths = useMemo(() => {
    const arr: { entry_id: string; path: string }[] = []
    ;(customEntries ?? []).forEach((entry) => {
      const raw = entry.data as unknown as {
        name?: string
        template?: CustomSymbolTemplate
        data?: { name?: string; template?: CustomSymbolTemplate }
      }
      const t: CustomSymbolTemplate | undefined =
        raw?.template || raw?.data?.template
      if ((t?.kind ?? 'svg') === 'image' && t?.image?.path) {
        arr.push({ entry_id: entry.id, path: t.image.path })
      }
    })
    return arr.length ? arr : undefined
  }, [customEntries])

  const { results: signedUrlResults } = useRolodexSignedURLs(imageFilePaths)

  const signedUrlMap = useMemo(() => {
    const m = new Map<string, string>()
    ;(signedUrlResults ?? []).forEach((r) => {
      m.set(`${r.entry_id}:${r.path}`, r.signed_url)
    })
    return m
  }, [signedUrlResults])

  const customSymbols: SymbolDefinition[] = useMemo(() => {
    if (!customEntries?.length) return []
    const defs = customEntries
      .map((entry) => {
        const raw = entry.data as unknown as {
          name?: string
          template?: CustomSymbolTemplate
          data?: { name?: string; template?: CustomSymbolTemplate }
        }
        // Support both shapes: { name, template } or { data: { name, template } }
        const template: CustomSymbolTemplate | undefined =
          raw?.template || raw?.data?.template
        if (!template) return null

        const component = ((props: NodeProps<PfdNode>) => (
          <CustomSymbolNode {...props} template={template} />
        )) as React.ComponentType<NodeProps<PfdNode>>

        const previewComponent = () =>
          (template.kind ?? 'svg') === 'image' ? (
            <img
              alt='preview'
              src={
                template.imageDataUrl ||
                signedUrlMap.get(`${entry.id}:${template.image?.path ?? ''}`) ||
                ''
              }
              style={{ width: 32, height: 32, objectFit: 'contain' }}
            />
          ) : (
            <div
              style={{ width: 32, height: 32 }}
              dangerouslySetInnerHTML={{
                __html: (template.svg || '').replace(
                  /color:\s*#ffffff/gi,
                  'color: currentColor'
                ),
              }}
            />
          )

        const def: SymbolDefinition = {
          id: entry.id,
          name: raw?.name || raw?.data?.name || entry.id,
          description: 'Custom PFD node',
          category: 'custom',
          tags: ['custom'],
          component,
          previewComponent,
        }
        // Ensure React Flow nodeTypes contains this id
        registerCustomSymbol(def)
        return def
      })
      .filter(Boolean) as SymbolDefinition[]
    return defs
  }, [customEntries, signedUrlMap])

  const computedCategories = useMemo(() => {
    const base = SYMBOL_CATEGORIES
    if (!customSymbols.length) return base
    return [
      {
        id: 'custom',
        name: 'Custom',
        description: 'Your imported symbols',
        symbols: customSymbols,
      },
      ...base,
    ]
  }, [customSymbols])

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return computedCategories
    }

    const query = searchQuery.toLowerCase()
    return computedCategories
      .map((category) => ({
        ...category,
        symbols: category.symbols.filter(
          (symbol) =>
            symbol.name.toLowerCase().includes(query) ||
            symbol.description?.toLowerCase().includes(query)
        ),
      }))
      .filter((category) => category.symbols.length > 0)
  }, [searchQuery, computedCategories])

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId)
    } else {
      newExpanded.add(categoryId)
    }
    setExpandedCategories(newExpanded)
  }

  const handleSymbolDragStart = (
    event: React.DragEvent,
    symbol: SymbolDefinition
  ) => {
    event.dataTransfer.setData('application/reactflow', symbol.id)
    event.dataTransfer.effectAllowed = 'move'
    onDragStart(event, symbol.id)
  }

  const totalSymbols = filteredCategories.reduce(
    (sum, cat) => sum + cat.symbols.length,
    0
  )

  return (
    <LibraryContainer>
      <LibraryHeader>
        <HeaderRow>
          <LibraryTitle>Symbol Library</LibraryTitle>
          <CantileverButton
            iconOnly
            onClick={() =>
              onImportNode
                ? onImportNode()
                : setDrawerState(DrawerState.ImportSymbol)
            }
            title='Import node'
            aria-label='Import node'>
            <Plus size={16} />
          </CantileverButton>
        </HeaderRow>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type='text'
            placeholder='Search symbols...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>
      </LibraryHeader>

      <CategoriesContainer>
        {filteredCategories.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon size={24} />
            No symbols found
            <EmptyStateSubtext>Try different search terms</EmptyStateSubtext>
          </EmptyState>
        ) : (
          filteredCategories.map((category) => {
            const isExpanded = expandedCategories.has(category.id)
            return (
              <div key={category.id}>
                <CategoryHeader
                  $isExpanded={isExpanded}
                  onClick={() => toggleCategory(category.id)}>
                  <span>
                    {category.name} ({category.symbols.length})
                  </span>
                  {isExpanded ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </CategoryHeader>

                <CategoryContent $isExpanded={isExpanded}>
                  <SymbolsGrid>
                    {category.symbols.map((symbol) => (
                      <SymbolItem
                        key={symbol.id}
                        draggable
                        onDragStart={(event) =>
                          handleSymbolDragStart(event, symbol)
                        }
                        title={symbol.description || symbol.name}>
                        <SymbolPreview>
                          <symbol.previewComponent />
                        </SymbolPreview>
                        <SymbolLabel>{symbol.name}</SymbolLabel>
                      </SymbolItem>
                    ))}
                  </SymbolsGrid>
                </CategoryContent>
              </div>
            )
          })
        )}
      </CategoriesContainer>

      {searchQuery && (
        <FooterContainer>
          {totalSymbols} symbol{totalSymbols !== 1 ? 's' : ''} found
        </FooterContainer>
      )}
    </LibraryContainer>
  )
}
